/**
 * MessageTimeline — peces compartides de l'historial de comunicacions
 * (ReservationMessagesTab i ClientMessagesTab; plans/whatsapp-capture.md A4.1).
 *
 * - `ChannelFilterChips`: Tots / WhatsApp / OTA / Email (grups del backend).
 * - `MessageTimeline`: entrades agrupades per dia + «Carregar anteriors» amb el cursor.
 * - `CommunicationLogEntry`: un missatge unitari (WhatsApp importat, OTA) en una línia
 *   expandible amb la direcció visible, o una conversa enganxada (targeta d'abans).
 * - `MessagingTaskEntry`: una tasca de missatgeria del sistema.
 * - `buildTimeline`: barreja pàgines de logs amb la resta d'entrades sense desordenar-les.
 *
 * L'inbox de WhatsApp (`plans/whatsapp-inbox.md` §4.2, A2.5 del runbook 3) reutilitza
 * `CommunicationLogEntry` amb camps opcionals (autor, cita, editat, revocat, mèdia, entrega).
 * Sense aquests camps el marcatge és exactament el d'abans (fitxa de reserva i de client).
 */

import { useState, type ReactNode, type Ref, type UIEvent } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Check,
  CheckCheck,
  ChevronDown,
  ClipboardPaste,
  Clock,
  CheckCircle2,
  Contact,
  FileText,
  Image as ImageIcon,
  Loader2,
  MapPin,
  MessageSquare,
  Mic,
  Phone,
  Quote,
  Send,
  Smile,
  Video,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { langToLocale } from '@/lib/dates';
import type {
  CommunicationLogChannelFilter,
  CommunicationLogListItem,
  MessagingTask,
  WaAuthor,
  WaDeliveryStatus,
  WaMessageType,
} from '@guilleumes/types';

const locale = langToLocale('ca');

// ═══════════════════════════════════════════════════════════════
// Filtre de canal
// ═══════════════════════════════════════════════════════════════

export const CHANNEL_FILTER_OPTIONS: { value: CommunicationLogChannelFilter | undefined; label: string }[] = [
  { value: undefined, label: 'Tots' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'OTA', label: 'OTA' },
  { value: 'EMAIL', label: 'Email' },
];

export function ChannelFilterChips({
  value,
  onChange,
}: {
  value: CommunicationLogChannelFilter | undefined;
  onChange: (value: CommunicationLogChannelFilter | undefined) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1" role="group" aria-label="Filtre de canal">
      {CHANNEL_FILTER_OPTIONS.map((option) => {
        const active = option.value === value;
        return (
          <Button
            key={option.label}
            type="button"
            size="sm"
            variant={active ? 'default' : 'outline'}
            aria-pressed={active}
            className="h-7 rounded-full px-3 text-xs"
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Línia de temps
// ═══════════════════════════════════════════════════════════════

export interface TimelineItem {
  id: string;
  date: Date;
  node: ReactNode;
}

/**
 * Barreja les files de logs carregades amb la resta d'entrades (tasques, casos).
 * Mentre quedin pàgines per carregar, les entrades més antigues que l'últim log carregat
 * s'amaguen: si no, apareixerien al final com si no hi hagués missatges entremig.
 */
export function buildTimeline(
  logs: TimelineItem[],
  others: TimelineItem[],
  hasMoreLogs: boolean,
): TimelineItem[] {
  const oldestLog = logs.length > 0 ? Math.min(...logs.map((l) => l.date.getTime())) : null;
  const visibleOthers =
    hasMoreLogs && oldestLog !== null
      ? others.filter((o) => o.date.getTime() >= oldestLog)
      : others;
  return [...logs, ...visibleOthers].sort((a, b) => b.date.getTime() - a.date.getTime());
}

/**
 * Etiqueta de la capçalera d'un dia (§4.2, `EEEE d MMMM`, `ca`): amb l'any només quan no és
 * l'actual («dilluns 3 de juny de 2024» vs «dimecres 17 de setembre»).
 */
export function timelineDayLabel(date: Date, now: Date = new Date()): string {
  const pattern = date.getFullYear() === now.getFullYear() ? 'EEEE d MMMM' : 'EEEE d MMMM yyyy';
  return format(date, pattern, { locale });
}

/**
 * Classes de la capçalera fixa d'un dia: fons opac i per sobre de les bombolles quan s'hi
 * enganxa (en repòs ocupa la seva pròpia línia i no tapa res).
 */
export const TIMELINE_DAY_HEADER_CLASS =
  'sticky top-0 z-20 bg-background py-1 text-xs font-medium text-muted-foreground';

export function MessageTimeline({
  items,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  className,
}: {
  items: TimelineItem[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  className?: string;
}) {
  const groups: { key: string; date: Date; items: TimelineItem[] }[] = [];
  for (const item of items) {
    const key = format(item.date, 'yyyy-MM-dd');
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(item);
    else groups.push({ key, date: item.date, items: [item] });
  }

  return (
    <div className={cn('min-w-0 space-y-3', className)}>
      {groups.map((group) => (
        <section key={group.key} className="min-w-0" aria-label={format(group.date, 'd MMMM yyyy', { locale })}>
          <h4 className={TIMELINE_DAY_HEADER_CLASS}>{timelineDayLabel(group.date)}</h4>
          <div className="space-y-1.5">
            {group.items.map((item) => (
              <div key={item.id} className="min-w-0">
                {item.node}
              </div>
            ))}
          </div>
        </section>
      ))}
      {hasNextPage && (
        <div className="flex justify-center pt-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            className="gap-2"
          >
            {isFetchingNextPage && <Loader2 className="h-4 w-4 animate-spin" />}
            Carregar anteriors
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Contenidor amb scroll vertical de la línia de temps. No és el `ScrollArea` de Radix: el seu
 * Viewport embolcalla el contingut amb un `div` amb `display: table; min-width: 100%`, que creix
 * amb el text més llarg i anul·la el `truncate` de les files (scroll horitzontal a 385 px).
 */
export function TimelineScroll({
  className,
  children,
  ref,
  onScroll,
}: {
  className?: string;
  children: ReactNode;
  /** L'inbox l'usa per ancorar el scroll en carregar pàgines anteriors. */
  ref?: Ref<HTMLDivElement>;
  onScroll?: (event: UIEvent<HTMLDivElement>) => void;
}) {
  return (
    <div
      ref={ref}
      onScroll={onScroll}
      className={cn('min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain', className)}
    >
      {children}
    </div>
  );
}

export type TimelineOwner = 'reservation' | 'client';

/**
 * Text de l'estat buit segons el filtre. Els d'OTA i Email no conviden a enganxar un WhatsApp:
 * no hi tenen res a veure.
 */
export function emptyTimelineText(
  channel: CommunicationLogChannelFilter | undefined,
  owner: TimelineOwner,
): { title: string; pasteHint: boolean } {
  const whose = owner === 'reservation' ? 'aquesta reserva' : 'aquest client';
  switch (channel) {
    case 'OTA':
      return { title: `No hi ha missatges d'OTA per ${whose}.`, pasteHint: false };
    case 'EMAIL':
      return { title: `No hi ha correus per ${whose}.`, pasteHint: false };
    case 'WHATSAPP':
      return { title: `No hi ha comunicacions d'aquest canal per ${whose}.`, pasteHint: true };
    default:
      return {
        title:
          owner === 'reservation'
            ? 'No hi ha comunicacions ni casos registrats per aquesta reserva.'
            : 'No hi ha comunicacions registrades per aquest client.',
        pasteHint: true,
      };
  }
}

/** La invitació a enganxar una conversa (A4.5: també sota les tasques quan no hi ha cap missatge). */
export function PasteHint({ className }: { className?: string }) {
  return (
    <p className={cn('text-xs text-muted-foreground', className)}>
      Enganxa una conversa de WhatsApp per començar.
    </p>
  );
}

export function EmptyTimeline({
  channel,
  owner,
  className,
}: {
  channel: CommunicationLogChannelFilter | undefined;
  owner: TimelineOwner;
  className?: string;
}) {
  const { title, pasteHint } = emptyTimelineText(channel, owner);
  return (
    <div className={cn('flex flex-col items-center justify-center text-center', className)}>
      <FileText className="mb-3 h-10 w-10 text-muted-foreground/30" />
      <p className="text-sm text-muted-foreground">{title}</p>
      {pasteHint && <PasteHint className="mt-1" />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Entrades
// ═══════════════════════════════════════════════════════════════

function channelLabel(log: CommunicationLogListItem): string {
  if (log.channel === 'WHATSAPP' || (!log.channel && log.source === 'WHATSAPP_PASTE')) {
    return 'WhatsApp';
  }
  if (log.channel?.startsWith('OTA_')) {
    const ota = log.channel.slice(4);
    return ota.charAt(0) + ota.slice(1).toLowerCase();
  }
  if (log.channel === 'EMAIL') return 'Email';
  return log.channel ?? log.source;
}

function channelBorder(log: CommunicationLogListItem): string {
  if (log.channel === 'WHATSAPP') return 'border-l-green-500';
  if (log.channel?.startsWith('OTA_')) return 'border-l-sky-500';
  if (log.channel === 'EMAIL') return 'border-l-amber-500';
  return 'border-l-purple-400';
}

// ── Camps de l'inbox (A2.5) ─────────────────────────────────────

/** Camps opcionals que porta un missatge de l'inbox (`WaMessageItem`). */
export interface WaTimelineFields {
  author?: WaAuthor | null;
  waMessageId?: string | null;
  quotedWaMessageId?: string | null;
  /** Text de l'original citat, si és a les pàgines carregades (el resol el fil). */
  quotedText?: string | null;
  editedAt?: string | null;
  revokedAt?: string | null;
  waMessageType?: WaMessageType | null;
  waStatus?: WaDeliveryStatus | null;
  waError?: string | null;
  sentBy?: { id: string; name: string | null } | null;
}

export type TimelineLog = CommunicationLogListItem & WaTimelineFields;

/** `id` de l'element d'un missatge al fil, per saltar-hi des d'una cita. */
export function waMessageAnchorId(waMessageId: string): string {
  return `wa-msg-${waMessageId}`;
}

/** Etiqueta d'autor d'un sortint (§4.2): «App», «PMS · nom», «Miranda». */
export function authorLabel(log: TimelineLog): string | null {
  if (log.direction !== 'OUTBOUND' || !log.author) return null;
  switch (log.author) {
    case 'APP':
      return 'App';
    case 'PMS_USER':
      return log.sentBy?.name ? `PMS · ${log.sentBy.name}` : 'PMS';
    case 'MIRANDA':
      return 'Miranda';
    default:
      return null;
  }
}

const MEDIA_ICONS: Partial<Record<WaMessageType, { icon: LucideIcon; label: string }>> = {
  image: { icon: ImageIcon, label: 'Imatge' },
  video: { icon: Video, label: 'Vídeo' },
  audio: { icon: Mic, label: 'Àudio' },
  document: { icon: FileText, label: 'Document' },
  sticker: { icon: Smile, label: 'Adhesiu' },
  location: { icon: MapPin, label: 'Ubicació' },
  contacts: { icon: Contact, label: 'Contacte' },
};

function DeliveryTicks({ status, error }: { status: WaDeliveryStatus; error?: string | null }) {
  switch (status) {
    case 'SENT':
      return <Check className="h-3.5 w-3.5 text-muted-foreground" aria-label="Enviat" />;
    case 'DELIVERED':
      return <CheckCheck className="h-3.5 w-3.5 text-muted-foreground" aria-label="Entregat" />;
    case 'READ':
      return <CheckCheck className="h-3.5 w-3.5 text-blue-500" aria-label="Llegit" />;
    case 'FAILED':
      return (
        <span title={error ?? "Error d'entrega"} className="inline-flex">
          <AlertTriangle className="h-3.5 w-3.5 text-red-600" aria-label="Error d'entrega" />
        </span>
      );
    default:
      return null;
  }
}

function scrollToQuoted(waMessageId: string) {
  document
    .getElementById(waMessageAnchorId(waMessageId))
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/** Un missatge unitari en una línia; clic per desplegar el text sencer. */
function SingleMessageEntry({
  log,
  defaultExpanded,
  canRevealRevoked,
}: {
  log: TimelineLog;
  defaultExpanded?: boolean;
  canRevealRevoked?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const [revealed, setRevealed] = useState(false);
  const inbound = log.direction === 'INBOUND';
  const outbound = log.direction === 'OUTBOUND';
  const date = new Date(log.loggedAt);
  const author = authorLabel(log);
  const miranda = outbound && log.author === 'MIRANDA';
  const revoked = !!log.revokedAt;
  const media = log.waMessageType ? MEDIA_ICONS[log.waMessageType] : undefined;

  const line = (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
      className={cn(
        // min-w-0 + overflow-hidden: la fila no pot ser més ampla que el panell encara que el text sí.
        'flex w-full min-w-0 max-w-full items-start gap-2 overflow-hidden rounded-md border border-l-4 px-3 py-1.5 text-left text-sm hover:bg-muted/50',
        // Desplegat, el text baixa a una línia pròpia (D3): al fil de l'inbox (≈300 px) les
        // etiquetes i l'hora el deixaven a un caràcter d'amplada. Plegat, tot en una línia.
        expanded && 'flex-wrap gap-y-1',
        channelBorder(log),
        outbound && 'bg-muted/30',
        miranda && 'bg-purple-50',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex shrink-0 items-center gap-1 text-xs font-medium',
          inbound ? 'text-blue-600' : outbound ? 'text-green-700' : 'text-muted-foreground',
        )}
        title={inbound ? 'Entrant' : outbound ? 'Sortint' : undefined}
      >
        {inbound ? (
          <ArrowDownLeft className="h-3.5 w-3.5" aria-hidden />
        ) : outbound ? (
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
        ) : null}
        <span className="sr-only sm:not-sr-only">{inbound ? 'Entrant' : outbound ? 'Sortint' : ''}</span>
      </span>
      {author && (
        <span
          className={cn(
            'mt-0.5 shrink-0 rounded px-1 text-[10px] font-medium',
            miranda ? 'bg-purple-200 text-purple-900' : 'bg-muted text-muted-foreground',
          )}
        >
          {author}
        </span>
      )}
      {media && !revoked && (
        <media.icon
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground"
          aria-label={media.label}
        />
      )}
      <span
        className={cn(
          // Plegat: flex-1 + min-w-0, una línia amb «…». Desplegat: tota l'amplada, sota la capçalera.
          'block min-w-0',
          expanded
            ? 'order-last basis-full whitespace-pre-wrap break-words [overflow-wrap:anywhere]'
            : 'flex-1 truncate',
          revoked && !revealed && 'italic text-muted-foreground',
        )}
      >
        {revoked && !revealed ? 'Missatge eliminat' : log.rawContent}
      </span>
      <span
        className={cn(
          'flex shrink-0 items-center gap-1 whitespace-nowrap text-xs text-muted-foreground',
          expanded && 'ml-auto',
        )}
      >
        {log.editedAt && (
          <span
            className="italic"
            title={format(new Date(log.editedAt), 'dd/MM/yyyy HH:mm', { locale })}
          >
            editat {format(new Date(log.editedAt), 'HH:mm', { locale })}
          </span>
        )}
        <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
          {channelLabel(log)}
        </Badge>
        <time dateTime={log.loggedAt}>{format(date, 'HH:mm', { locale })}</time>
        {log.waStatus && <DeliveryTicks status={log.waStatus} error={log.waError} />}
        <ChevronDown
          className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')}
          aria-hidden
        />
      </span>
    </button>
  );

  const quoted = log.quotedWaMessageId;
  const revealButton = revoked && canRevealRevoked;
  if (!quoted && !revealButton) return line;

  // La cita i el «mostra» van fora del botó de la línia: un botó dins d'un altre no és vàlid.
  return (
    <div className="min-w-0 space-y-0.5">
      {quoted && (
        <button
          type="button"
          onClick={() => scrollToQuoted(quoted)}
          className="flex w-full min-w-0 items-center gap-1 rounded border-l-2 border-l-muted-foreground/40 bg-muted/40 px-2 py-0.5 text-left text-xs text-muted-foreground hover:bg-muted"
          aria-label="Ves al missatge citat"
        >
          <Quote className="h-3 w-3 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{log.quotedText ?? 'Missatge citat'}</span>
        </button>
      )}
      {line}
      {revealButton && (
        <button
          type="button"
          onClick={() => setRevealed((v) => !v)}
          className="text-xs text-muted-foreground underline"
        >
          {revealed ? 'amaga' : 'mostra'}
        </button>
      )}
    </div>
  );
}

/** Conversa enganxada a mà (targeta, com abans). */
function PastedConversationEntry({ log }: { log: CommunicationLogListItem }) {
  const parsedCount = log.parsedMessages?.length ?? 0;
  const date = new Date(log.loggedAt);

  return (
    <Card className="border-l-4 border-l-purple-400">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <ClipboardPaste className="h-4 w-4 shrink-0 text-purple-500" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">Conversa WhatsApp enganxada</p>
              <p className="truncate text-xs text-muted-foreground">
                {log.summary || `${parsedCount} missatges parsejats`}
              </p>
            </div>
          </div>
          <div className="shrink-0 whitespace-nowrap text-right">
            <p className="text-xs text-muted-foreground">
              {format(date, 'dd/MM/yyyy HH:mm', { locale })}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(date, { addSuffix: true, locale })}
            </p>
          </div>
        </div>
        {log.phoneNumber && (
          <div className="mt-2 flex items-center gap-1">
            <Phone className="h-3 w-3 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">{log.phoneNumber}</p>
          </div>
        )}
        {log.loggedBy?.name && (
          <p className="mt-1 text-xs text-muted-foreground">Per: {log.loggedBy.name}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function CommunicationLogEntry({
  log,
  defaultExpanded,
  canRevealRevoked,
}: {
  log: TimelineLog;
  /** L'inbox obre els missatges desplegats. */
  defaultExpanded?: boolean;
  /** ADMIN: pot veure el text d'un missatge revocat. */
  canRevealRevoked?: boolean;
}) {
  if (log.source === 'WHATSAPP_PASTE') return <PastedConversationEntry log={log} />;
  return (
    <SingleMessageEntry
      log={log}
      defaultExpanded={defaultExpanded}
      canRevealRevoked={canRevealRevoked}
    />
  );
}

const KIND_LABELS: Record<string, string> = {
  FIRST_CONTACT: 'Primer contacte',
  WELCOME: 'Benvinguda',
  GUEST_FORM: 'Registre',
  ACCESS: 'Accés',
  PAYMENT_LINK: 'Pagament',
  REVIEW: 'Ressenya',
  CUSTOM: 'Personalitzat',
};

const STATUS_LABELS: Record<string, string> = {
  done: 'Enviat',
  pending: 'Pendent',
  cancelled: 'Cancel·lat',
  in_progress: 'En curs',
  snoozed: 'Posposat',
};

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'done':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'pending':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'cancelled':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'in_progress':
      return <Send className="h-4 w-4 text-blue-500" />;
    default:
      return <Clock className="h-4 w-4 text-muted-foreground" />;
  }
}

export function MessagingTaskEntry({ task }: { task: MessagingTask }) {
  return (
    <Card className="border-l-4 border-l-blue-400">
      <CardContent className="p-3">
        {/* A4.5: el títol té la seva línia; distintiu, estat i data a sota (a 385 px no hi cabien al costat). */}
        <div className="flex min-w-0 items-start gap-2">
          <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
          <div className="min-w-0 flex-1">
            <p className="break-words text-sm font-medium [overflow-wrap:anywhere]">{task.title}</p>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <Badge variant="outline" className="whitespace-nowrap text-xs">
                {KIND_LABELS[task.messageKind] ?? task.messageKind}
              </Badge>
              <span className="flex items-center gap-1 whitespace-nowrap">
                <StatusIcon status={task.status} />
                {STATUS_LABELS[task.status] ?? task.status}
              </span>
              <time className="whitespace-nowrap" dateTime={task.sentAt || task.createdAt}>
                {format(new Date(task.sentAt || task.createdAt), 'dd/MM/yyyy HH:mm', { locale })}
              </time>
              {task.sentAt && task.sentByName && (
                <span className="whitespace-nowrap">Per: {task.sentByName}</span>
              )}
            </div>
          </div>
        </div>
        {task.recipientPhone && (
          <div className="mt-2 flex items-center gap-1">
            <Phone className="h-3 w-3 text-muted-foreground" />
            <p className="truncate text-xs text-muted-foreground">{task.recipientPhone}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
